import React, { useEffect, Suspense, useRef } from 'react'
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from 'react-router-dom'
import {
  useAuth,
  PendingSubmissionsContextProvider,
  DraftsContextProvider,
} from '@oneblink/apps-react'
import AuthenticatedRoute from 'components/AuthenticatedRoute'
import OAuthCallback from 'components/Auth/OAuthCallback'
import OfflineBar from 'components/OfflineBar'

import ThemeProvider from 'Providers/ThemeProvider'
import FormDefinitionProvider from 'Providers/FormDefinitionProvider'
import ReferenceDataProvider from 'Providers/ReferenceDataProvider'

import Page from 'components/Layout/Page'
import { H1 } from 'components/TextComponents'

import LoginScene from 'Scenes/Auth/Login'
import LogoutScene from 'Scenes/Auth/Logout'
import FormsListScene from 'Scenes/FormsList'
import DraftsScene from 'Scenes/Drafts'
import ProfileScene from 'Scenes/Profile'
import PendingQueueListScene from 'Scenes/Pending'
import VetDeclarationValidation from 'Scenes/VetDeclaration/Validation'
import VetDeclarationReceipt from 'Scenes/Receipts/VetDeclarationReceipt'
import CattleTickFormReceipt from 'Scenes/Receipts/CattleTickFormReceipt'

import { ToastContainer, Slide } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import * as serviceWorker from './serviceWorkerRegistration'

import '@oneblink/apps-react/dist/styles.css'

import FormScene from 'Scenes/Form/FormContainer'
import config from 'config'

export default function App() {
  const hasInstalledServiceWorker = useRef(false)
  const { isLoggedIn } = useAuth()

  useEffect(() => {
    if (hasInstalledServiceWorker.current) return
    console.log('installing service worker')
    serviceWorker.register({
      onSuccess: () => {
        console.log('service worker installed')
      },
    })
    hasInstalledServiceWorker.current = true
  }, [])

  return (
    <Router>
      <ThemeProvider>
        <OfflineBar />
        <PendingSubmissionsContextProvider
          isPendingQueueEnabled
          successNotificationTimeoutMs={3000}
        >
          <DraftsContextProvider
            formsAppId={config.OB_FORMS_APP_ID}
            isDraftsEnabled
          >
            <Page>
              <Switch>
                <Route path="/declaration" exact>
                  <VetDeclarationValidation />
                </Route>
                <Route path="/declaration/submitted">
                  <VetDeclarationReceipt />
                </Route>
                <Route path="/callback">
                  <OAuthCallback />
                </Route>
                <Route path="/login">
                  <LoginScene />
                </Route>
                <Route path="/logout" exact>
                  <LogoutScene />
                </Route>
                {!isLoggedIn && (
                  <Route path="/">
                    <Redirect to="/login" />
                  </Route>
                )}
                {isLoggedIn && (
                  <>
                    <Switch>
                      <AuthenticatedRoute path="/profile">
                        <ProfileScene />
                      </AuthenticatedRoute>
                    </Switch>
                    <ReferenceDataProvider>
                      <FormDefinitionProvider>
                        <Switch>
                          <AuthenticatedRoute path="/forms/:formId" exact>
                            <Suspense fallback={null}>
                              <FormScene />
                            </Suspense>
                          </AuthenticatedRoute>
                          <AuthenticatedRoute
                            path="/forms/:formId/receipt"
                            exact
                          >
                            <CattleTickFormReceipt />
                          </AuthenticatedRoute>
                          <AuthenticatedRoute path="/drafts" exact>
                            <DraftsScene />
                          </AuthenticatedRoute>
                          <AuthenticatedRoute path="/pending" exact>
                            <PendingQueueListScene />
                          </AuthenticatedRoute>
                          <AuthenticatedRoute path="/" exact>
                            <H1>Animal Movements</H1>
                            <FormsListScene />
                          </AuthenticatedRoute>
                        </Switch>
                      </FormDefinitionProvider>
                    </ReferenceDataProvider>
                  </>
                )}
              </Switch>
            </Page>
            <ToastContainer transition={Slide} />
          </DraftsContextProvider>
        </PendingSubmissionsContextProvider>
      </ThemeProvider>
    </Router>
  )
}
